import React, { useState } from 'react';
import styles from './side-popup.module.scss';
import { Form } from 'react-bootstrap';
import { availableModulesList, IUnAuthenticatedUserModel, initialUnAuthenticatedUser } from '../grant-access.model';
import PhoneInput from '@sssuite-component-ui/react-phone-input-2';
import { ButtonComponent } from 'cp-common-ui-components';
import { handleInvalidCountryCodeEntered, isValidEmailAddress } from 'src/helper/utils';
import { isValidMobileNumber } from '@sssuite-js-packages/mobile-number-validator';
import { CountryData } from 'react-phone-input-2';
import CustomMultiSelectDropdown, { ICustomDropdownOption } from 'src/components/custom-multi-select-dropdown';
import { changeTypeOfAccessibleModules } from './helper';
import { UTENotifier } from 'src/modules/notification/ute-notifier';
import { GRANTACCESSCONSTANTS } from 'src/helper/constants';
import { addNewThirdPartyUser, updateThirdPartyUser } from '../grant-access-api';
import UpdateConfirmPopup from '../update-confirm-popup';
import { logger } from 'src/context/ute-auth-context/auth-provider';

interface ISidePopupProps {
	fetchThirdPartyAuthorizedUsers: () => void;
	toggleSidePopupVisibility: (value: boolean) => void;
	userDetails: IUnAuthenticatedUserModel;
	isEdit: boolean;
	resetRequiredStates: () => void;
}

const SidePopup: React.FC<ISidePopupProps> = ({
	fetchThirdPartyAuthorizedUsers,
	userDetails,
	isEdit,
	resetRequiredStates,
}) => {
	const [newUserDetails, setNewUserDetails] = useState<IUnAuthenticatedUserModel>(userDetails);
	const [userCountryCode, setUserCountryCode] = useState<string>(`+${newUserDetails.countryCode}`);
	const [userMobile, setUserMobile] = useState<string>(newUserDetails.mobileNumber);
	const [mobileNumberFullValue, setMobileNumberFullValue] = useState<string>(`${userCountryCode}${userMobile}`);
	const [isUserUpdated, setIsUserUpdated] = useState<Boolean>(false);
	const [accessibleModules, setAccessibleModules] = useState<ICustomDropdownOption[]>(
		changeTypeOfAccessibleModules(
			newUserDetails.accessibleModules,
			availableModulesList.map((module) => module.label),
		),
	);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState<boolean>(false);

	const [mandatoryFieldError, setMandatoryFieldError] = useState({
		name: { error: false, message: '' },
		emailAddress: { error: false, message: '' },
		countryCode: { error: false, message: '' },
		mobileNumber: { error: false, message: '' },
		accessibleModules: { error: false, message: '' },
	});

	const handleInputChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewUserDetails({
			...newUserDetails,
			[e.target.name]: e.target.value,
		});
		isEdit && setIsUserUpdated(true);
	};

	const handleMobileInputChanges = (fullValue: string, countryObject: CountryData): void => {
		fullValue = handleInvalidCountryCodeEntered(fullValue, countryObject);
		setUserCountryCode(`+${countryObject.dialCode}`);
		setUserMobile(fullValue.slice(countryObject.dialCode.length));
		setNewUserDetails({
			...newUserDetails,
			countryCode: fullValue ? `+${countryObject.dialCode}` : '',
			mobileNumber: fullValue.slice(countryObject.dialCode.length),
		});
		setMobileNumberFullValue(fullValue);
		isEdit && setIsUserUpdated(true);
	};

	const handleOnBlurName = (id: string, value: string): void => {
		value = value.trim();
		if (value.length === 0) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'This field is required.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = false;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = '';
			setMandatoryFieldError(newMandatoryFieldError);
			setNewUserDetails({ ...newUserDetails, name: value });
		}
	};

	const handleOnBlurEmail = (id: string, value: string): void => {
		value = value.trim();
		if (value.length === 0) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'This field is required.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else if (!isValidEmailAddress(value)) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'Please fill valid Email Address.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = false;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = '';
			setMandatoryFieldError(newMandatoryFieldError);
			setNewUserDetails({ ...newUserDetails, emailAddress: value });
		}
	};

	const handleOnBlurMobile = (id: string): void => {
		if (userMobile.trim().length === 0) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'This field is required.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else if (
			userMobile &&
			userMobile.trim().length &&
			(!userCountryCode || (userCountryCode && userCountryCode === ''))
		) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'Please select country code.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else if (userMobile && userCountryCode && !isValidMobileNumber(userCountryCode, userMobile)) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message =
				'Please enter a valid mobile number, according to the country code selected.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = false;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = '';
			setMandatoryFieldError(newMandatoryFieldError);
		}
	};

	const handleOnBlurPageAccess = (id: string, value: ICustomDropdownOption[]): void => {
		if (value.length === 0) {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = true;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = 'This field is required.';
			setMandatoryFieldError(newMandatoryFieldError);
		} else {
			const newMandatoryFieldError = { ...mandatoryFieldError };
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].error = false;
			newMandatoryFieldError[id as keyof typeof newMandatoryFieldError].message = '';
			setMandatoryFieldError(newMandatoryFieldError);
			setNewUserDetails({
				...newUserDetails,
				accessibleModules: value
					.filter((i) => i.value != -1)
					.map((x) => x.value)
					.join(','),
			});
		}
	};

	const handlePageAccessChanges = (option: ICustomDropdownOption[]): void => {
		setAccessibleModules(option);
		isEdit && setIsUserUpdated(true);
	};

	const checkAllMandatoryFields = (): boolean => {
		handleOnBlurName('name', newUserDetails.name);
		handleOnBlurEmail('emailAddress', newUserDetails.emailAddress);
		handleOnBlurMobile('mobileNumber');
		handleOnBlurPageAccess('accessibleModules', accessibleModules);
		for (const field in mandatoryFieldError)
			if (mandatoryFieldError[field as keyof typeof mandatoryFieldError].error) return false;

		return true;
	};

	const handleAddUser = (): void => {
		setIsLoading(true);
		if (checkAllMandatoryFields()) {
			addNewThirdPartyUser(
				newUserDetails,
				() => {
					UTENotifier.Success(GRANTACCESSCONSTANTS.SUCCESS_ADDING_USER);
					logger && logger.trackTrace(GRANTACCESSCONSTANTS.SUCCESS_ADDING_USER);
					fetchThirdPartyAuthorizedUsers();
					setIsLoading(false);
					resetRequiredStates();
				},
				(errorResponse: { status: number; data: string } | undefined) => {
					setIsLoading(false);
					logger && logger.trackWarning(GRANTACCESSCONSTANTS.ERROR_ADDING_USER);
					if (errorResponse?.status === 400) {
						setMandatoryFieldError({
							...mandatoryFieldError,
							emailAddress: {
								error: true,
								message: errorResponse?.data,
							},
						});
					}
				},
			);
		} else {
			setIsLoading(false);
		}
	};

	const handleUpdateUser = (): void => {
		if (checkAllMandatoryFields()) {
			setShowConfirmModal(true);
		}
	};

	const handleCancelUpdate = (): void => {
		setShowConfirmModal(false);
	};

	const handleConfirmUpdate = (): void => {
		setIsConfirmButtonDisabled(true);
		updateThirdPartyUser(
			newUserDetails,
			() => {
				setIsConfirmButtonDisabled(false);
				logger && logger.trackTrace(GRANTACCESSCONSTANTS.SUCCESS_UPDATING_USER);
				fetchThirdPartyAuthorizedUsers();
				setIsLoading(false);
				resetRequiredStates();
				setIsUserUpdated(false);
			},
			(errorResponse: { status: number; data: string } | undefined) => {
				setIsConfirmButtonDisabled(false);
				logger && logger.trackWarning(GRANTACCESSCONSTANTS.ERROR_UPDATING_USER);
				if (errorResponse?.status === 400) {
					setMandatoryFieldError({
						...mandatoryFieldError,
						emailAddress: {
							error: true,
							message: errorResponse?.data,
						},
					});
					setIsLoading(false);
					setShowConfirmModal(false);
				}
			},
		);
	};

	return (
		<>
			<section className={styles.sidePopupContainer}>
				<section className={styles.sidePopup}>
					<Form.Group
						controlId='name'
						className={styles.formGroup}>
						<Form.Label>Name</Form.Label>
						<Form.Control
							type='text'
							name='name'
							className={`${mandatoryFieldError.name.error ? styles.inputFieldErrorBorder : ''}`}
							placeholder='Name'
							value={newUserDetails.name}
							onChange={handleInputChanges}
							onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlurName(e.target.id, e.target.value)}
							maxLength={500}
						/>
						<div className={`${mandatoryFieldError.name.error ? styles.inputFieldErrorMessage : ''}`}>
							{mandatoryFieldError.name.message}
						</div>
					</Form.Group>
					<Form.Group
						controlId='emailAddress'
						className={styles.formGroup + ' mt-3'}>
						<Form.Label>Email ID</Form.Label>
						<Form.Control
							type='email'
							name='emailAddress'
							placeholder='Email ID'
							className={`${mandatoryFieldError.emailAddress.error ? styles.inputFieldErrorBorder : ''}`}
							value={newUserDetails.emailAddress}
							onChange={handleInputChanges}
							onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlurEmail(e.target.id, e.target.value)}
							maxLength={320}
						/>
						<div className={`${mandatoryFieldError.emailAddress.error ? styles.inputFieldErrorMessage : ''}`}>
							{mandatoryFieldError.emailAddress.message}
						</div>
					</Form.Group>
					<Form.Group
						controlId='mobileNumber'
						className={styles.mobileSection + ' mt-3 ' + styles.formGroup}>
						<label htmlFor='mobileNumber'>
							<span>Mobile</span>
						</label>
						<PhoneInput
							containerClass={`${mandatoryFieldError.mobileNumber.error ? 'mobileInputFieldErrorBorder' : ''} ${
								styles.inputCCMobile
							}`}
							inputClass={`${mandatoryFieldError.mobileNumber.error ? styles.mobileInputFieldErrorBorder : ''} ${
								styles.inputCCMobileField
							}`}
							country={'us'}
							autoFormat={true}
							value={mobileNumberFullValue}
							onChange={handleMobileInputChanges}
							onBlur={() => {
								handleOnBlurMobile('mobileNumber');
							}}
						/>
						<div className={`${mandatoryFieldError.mobileNumber.error ? styles.inputFieldErrorMessage : ''}`}>
							{mandatoryFieldError.mobileNumber.message}
						</div>
					</Form.Group>
					<Form.Group className={styles.accessibleModules + ' mt-3 ' + styles.formGroup}>
						<label htmlFor='pageAccess'>
							<span>Set Page Access</span>
						</label>
						<div className={styles.accessibleModules_infoText}>
							Access allows third parties to perform all actions on the page except signing
						</div>
						<CustomMultiSelectDropdown
							id='accessibleModules'
							selectedOptions={accessibleModules}
							setSelectedOption={handlePageAccessChanges}
							options={availableModulesList}
							borderColor={mandatoryFieldError.accessibleModules.error ? '#CC4A43' : '#898D91'}
							customPlaceHolder='Select Pages'
							disabled={false}
							handleOnBlur={() => handleOnBlurPageAccess('accessibleModules', accessibleModules)}
						/>
						<div className={`${mandatoryFieldError.accessibleModules.error ? styles.inputFieldErrorMessage : ''}`}>
							{mandatoryFieldError.accessibleModules.message}
						</div>
					</Form.Group>
					<ButtonComponent
						dataTestAuto='bf8596d1-4c0a-4e1e-9005-8918683f17de'
						onClick={isEdit ? handleUpdateUser : handleAddUser}
						variant={'primary'}
						buttonClassName={styles.sidePopupAddUserBtn}
						size={'medium'}
						disabled={isLoading || (isEdit && !isUserUpdated)}>
						{isEdit ? 'Update' : 'Add User'}
					</ButtonComponent>
				</section>
			</section>
			<UpdateConfirmPopup
				show={showConfirmModal}
				onClose={handleCancelUpdate}
				onConfirm={handleConfirmUpdate}
				confirmButtonDisabled={isConfirmButtonDisabled}
			/>
		</>
	);
};

export default SidePopup;
